// @ts-check
// Navegación de nivel superior entre los tres editores hermanos + el panel
// de progreso (TSK-0089, MOD-FRONTEND-CORE, docs/03-frontend.md §4
// "components no llaman fetch", §13 accesibilidad/teclado/foco). Componente
// PURO: no detecta la sección activa por sí mismo ni orquesta sesión -- ambas
// se reciben por parámetro (`opciones.seccionActiva` / `opciones.sesion`); la
// página que lo monta (TSK-0090) decide esos valores y le pasa, si procede,
// la pastilla de usuario/logout ya existente como `opciones.slotSesion`. Fija
// el contrato de rutas para BLK-02/BLK-03: 'Editor de diagramas' ->
// index.html, 'Pseudocódigo' -> pseudocode.html, 'Python' -> python.html.
//
// TSK-0141 (Ola 7, DEC-050/DEC-056): añade una cuarta entrada, 'progreso'
// (-> progress.html), que enlaza con el panel de progreso (TSK-0139/
// TSK-0140). Como esta barra deja de ser exclusivamente de "editores", el
// landmark `<nav>` pasa de `aria-label="Editores"` a `aria-label="Secciones"`
// (nombre accesible más preciso; el resto de contrato -- aria-current,
// estilo de sección activa no-cromático, slots, ausencia de fetch -- no
// cambia).
//
// No importa nada de services/**, state/** ni features/**, no llama a
// `fetch` ni a `window.location`, y no añade ningún listener propio: los
// `<a href>` nativos ya son operables por teclado sin JS adicional (por eso
// `destruir()` solo retira el elemento del DOM).
import { createSafeElement, setSafeAttribute } from '../security/safe-dom.js';
import { BLOQUES_INFO } from '../utils/bloques.js';
import { crearIcono } from './base/iconos.js';
import './base/segmentos.css';
import './button.css';
import './top-nav.css';

/** Nombre de la plataforma: el de la marca y el del `<title>` del sitio. */
export const MARCA_PLATAFORMA = 'Plataforma educativa de programación';

/**
 * Las secciones de los tres bloques salen de `utils/bloques.js`; 'progreso' y
 * 'admin' no son bloques del itinerario y solo existen como sección, así que
 * se declaran aquí.
 * @typedef {import('../utils/bloques.js').SeccionBloque | 'progreso' | 'admin'} SeccionEditor
 */

/**
 * @typedef {Object} OpcionesNavegacionSuperior
 * @property {SeccionEditor | null} seccionActiva - Enlace de sección a marcar como activo (aria-current="page"). `null` = ninguno.
 * @property {'autenticada' | 'anonima'} sesion - Decide el área de sesión (login/registro vs. slot de usuario).
 * @property {HTMLElement} [slotSesion] - Elemento ya construido por la página (p. ej. pastilla de usuario/logout) que se inserta en el área de sesión cuando `sesion === 'autenticada'`. Si no se pasa, el área queda vacía.
 * @property {boolean} [mostrarSecciones] - Fase 3 (adición retrocompatible). Por defecto `true`: renderiza la lista de las cuatro secciones (comportamiento y DOM sin cambios). `false` = **cabecera reducida**: NO renderiza las secciones ni ningún `aria-current`, y en su lugar muestra la marca (`marca`) como texto no interactivo; el `aria-label` del `<nav>` pasa de `"Secciones"` a `"Navegación"`. Pensado para páginas anónimas (login/registro) donde mostrar enlaces a secciones protegidas confundiría (todos redirigen a login).
 * @property {string} [marca] - Fase 3. Texto de marca, renderizado como `<span class="top-nav-marca">` NO interactivo (sin enlace) en el hueco izquierdo. Rediseño (Fase 4b): en el modo completo también va, delante de las secciones, con el nombre oculto a la vista (lo sigue leyendo el lector de pantalla). Por defecto, `MARCA_PLATAFORMA`.
 * @property {'ambos' | 'iniciar-sesion' | 'crear-cuenta'} [accesoSesion] - Fase 3. Solo afecta al área `sesion === 'anonima'`. Por defecto `'ambos'` (enlaces "Iniciar sesión" + "Crear cuenta", sin cambios). `'crear-cuenta'` = solo "Crear cuenta" (para login). `'iniciar-sesion'` = solo "Iniciar sesión" (para registro). Evita el enlace redundante a la propia página.
 * @property {boolean} [mostrarAdmin] - Adición retrocompatible. Por defecto `false`: la lista de secciones NO incluye el enlace 'Administración' (DOM y contrato "cuatro secciones" sin cambios). `true` = añade una quinta entrada 'Administración' (-> admin.html) justo tras 'Progreso'. La página que monta el componente decide este valor según el rol del usuario (`usuario.rol === 'admin'`), no el componente -- este solo lo pinta. Se ignora en modo reducido (`mostrarSecciones === false`, login/registro).
 */

/**
 * Contrato fijo de rutas de los tres editores hermanos más el panel de
 * progreso (TSK-0141, Ola 7, DEC-050/DEC-056).
 *
 * Los tres primeros se DERIVAN de `utils/bloques.js`: eran la copia de
 * referencia del mapa bloque -> página, y `components/progress-dashboard.js`
 * llegó a documentar por escrito que mantenía la suya "de forma
 * independiente" por no haber aquí un import público. Ahora las dos leen el
 * mismo dato. 'Progreso' se añade aquí porque no es un bloque del itinerario:
 * no tiene editor ni ejercicios, solo sección.
 * @type {ReadonlyArray<{ id: SeccionEditor, texto: string, href: string }>}
 */
const SECCIONES = [
  ...BLOQUES_INFO.map((bloque) => ({
    id: /** @type {SeccionEditor} */ (bloque.seccion),
    texto: bloque.textoNav,
    href: bloque.href,
  })),
  { id: /** @type {SeccionEditor} */ ('progreso'), texto: 'Progreso', href: 'progress.html' },
];

/**
 * Enlace de sección 'Administración' (-> admin.html). NO forma parte del
 * contrato base de `SECCIONES`: solo se añade a la lista cuando la página que
 * monta el componente pasa `mostrarAdmin: true` (rol admin). Se mantiene
 * separado para que el contrato "cuatro secciones por defecto" siga intacto.
 * @type {{ id: SeccionEditor, texto: string, href: string }}
 */
const SECCION_ADMIN = { id: 'admin', texto: 'Administración', href: 'admin.html' };

/**
 * Crea la lista de los tres enlaces de sección. El enlace de `seccionActiva`
 * recibe `aria-current="page"` más la clase `top-nav-link-activa`; si
 * `seccionActiva` es `null`, ningún enlace lleva `aria-current` ni esa clase.
 * Rediseño (Fase 4b): la lista es la navegación en segmentos de las piezas
 * base (`base/segmentos.css`), que pinta el activo por `aria-current` con
 * relleno y más peso, no solo con color (WCAG 2.2 SC 1.4.1).
 * @param {SeccionEditor | null} seccionActiva
 * @param {boolean} mostrarAdmin - Si `true`, añade la entrada 'Administración' tras las cuatro secciones base.
 * @returns {HTMLElement}
 */
function crearListaSecciones(seccionActiva, mostrarAdmin) {
  const lista = createSafeElement('ul', { className: 'top-nav-lista ui-segmentos' });

  const secciones = mostrarAdmin ? [...SECCIONES, SECCION_ADMIN] : SECCIONES;
  for (const seccion of secciones) {
    const esActiva = seccion.id === seccionActiva;
    const item = createSafeElement('li', { className: 'top-nav-item' });
    const enlace = createSafeElement('a', {
      className: esActiva
        ? 'top-nav-link top-nav-link-activa ui-segmento'
        : 'top-nav-link ui-segmento',
      text: seccion.texto,
    });
    setSafeAttribute(enlace, 'href', seccion.href);
    if (esActiva) {
      enlace.setAttribute('aria-current', 'page');
    }
    item.append(enlace);
    lista.append(item);
  }

  return lista;
}

/**
 * Crea el área de sesión. En `'anonima'` renderiza los enlaces "Iniciar
 * sesión" (`login.html`) y "Crear cuenta" (`register.html`). En
 * `'autenticada'` no renderiza esos enlaces; si se pasó `slotSesion`, ese
 * elemento (ya construido por la página que monta el componente) se inserta
 * tal cual -- este componente no lo crea ni le añade listeners.
 * @param {'autenticada' | 'anonima'} sesion
 * @param {HTMLElement} [slotSesion]
 * @param {'ambos' | 'iniciar-sesion' | 'crear-cuenta'} [accesoSesion] - Filtra los enlaces anónimos (Fase 3). Por defecto `'ambos'` (sin cambios).
 * @returns {HTMLElement}
 */
function crearAreaSesion(sesion, slotSesion, accesoSesion = 'ambos') {
  const area = createSafeElement('div', { className: 'top-nav-sesion' });

  if (sesion === 'anonima') {
    // Rediseño (Fase 4b): los dos accesos son enlaces con forma de TECLA
    // (`ui-button`, components/button.css). «Crear cuenta», la de acento.
    const enlaceLogin = createSafeElement('a', {
      className: 'top-nav-sesion-link ui-button ui-button-secundario',
      text: 'Iniciar sesión',
    });
    setSafeAttribute(enlaceLogin, 'href', 'login.html');

    const enlaceRegistro = createSafeElement('a', {
      className: 'top-nav-sesion-link top-nav-sesion-link-destacado ui-button ui-button-acento',
      text: 'Crear cuenta',
    });
    setSafeAttribute(enlaceRegistro, 'href', 'register.html');

    // Fase 3: `accesoSesion` filtra el enlace redundante a la propia página.
    // `'ambos'` (default) conserva el comportamiento previo (ambos enlaces).
    if (accesoSesion === 'iniciar-sesion') {
      area.append(enlaceLogin);
    } else if (accesoSesion === 'crear-cuenta') {
      area.append(enlaceRegistro);
    } else {
      area.append(enlaceLogin, enlaceRegistro);
    }
    return area;
  }

  if (slotSesion) {
    area.append(slotSesion);
  }
  return area;
}

/**
 * Marca no interactiva. Rediseño (Fase 4b): delante del nombre, una tecla con
 * el icono `<>`, decorativa (`aria-hidden` en el icono): el texto de la marca
 * sigue siendo exactamente `marca`. `compacta` (modo completo, con sesión):
 * el nombre se oculta a la vista, no al lector de pantalla, para dejar el
 * sitio a las secciones.
 * @param {string} marca
 * @param {{ compacta?: boolean }} [opciones]
 * @returns {HTMLElement}
 */
function crearMarca(marca, { compacta = false } = {}) {
  const elemento = createSafeElement('span', { className: 'top-nav-marca' });
  const tecla = createSafeElement('span', { className: 'top-nav-marca-tecla' });
  tecla.setAttribute('aria-hidden', 'true');
  tecla.append(crearIcono('codigo'));
  // El nombre, en su propio `span`: en pantallas muy estrechas se oculta a la
  // vista (no al lector) para que la tecla de acceso quepa en la misma fila.
  elemento.append(
    tecla,
    createSafeElement('span', {
      className: compacta ? 'top-nav-marca-texto u-visually-hidden' : 'top-nav-marca-texto',
      text: marca,
    }),
  );
  return elemento;
}

/**
 * Crea el componente de navegación superior: un único `<nav>` con los tres
 * enlaces de sección (con `aria-current="page"` en la sección activa) más un
 * bloque derecho con el área de sesión. Hasta DEC-179 admitía también
 * `slotAcciones` (el interruptor de tema, retirado en la Fase 2c): la barra
 * tiene ahora una forma fija con su sitio reservado (DEC-177), que no cuenta
 * con nada más a la derecha. Puramente presentacional --
 * no llama a `fetch`, no importa `services/**`/`state/**`/`features/**`, no
 * navega directamente.
 * @param {OpcionesNavegacionSuperior} opciones
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
export function crearNavegacionSuperior(opciones) {
  const {
    seccionActiva,
    sesion,
    slotSesion,
    mostrarSecciones = true,
    marca,
    accesoSesion = 'ambos',
    mostrarAdmin = false,
  } = opciones;

  // Modo completo (default): `<nav aria-label="Secciones">` con la lista.
  // Modo reducido (Fase 3, `mostrarSecciones === false`): sin secciones ni
  // `aria-current`; marca no interactiva a la izquierda y `aria-label`
  // "Navegación". La clase modificadora `top-nav-reducida` solo aparece en
  // el modo reducido (no cambia el DOM por defecto).
  const nav = createSafeElement('nav', {
    className: mostrarSecciones ? 'top-nav' : 'top-nav top-nav-reducida',
  });
  nav.setAttribute('aria-label', mostrarSecciones ? 'Secciones' : 'Navegación');

  // Modo completo: la marca compacta y las secciones, juntas en un bloque
  // izquierdo para que el `<nav>` siga teniendo dos hijos (ver más abajo).
  const elementoIzquierdo = mostrarSecciones
    ? createSafeElement('div', { className: 'top-nav-izquierda' })
    : crearMarca(marca ?? MARCA_PLATAFORMA);
  if (mostrarSecciones) {
    elementoIzquierdo.append(
      crearMarca(marca ?? MARCA_PLATAFORMA, { compacta: true }),
      crearListaSecciones(seccionActiva, mostrarAdmin),
    );
  }

  // La sesión, en un bloque derecho para que el `<nav>` conserve exactamente
  // dos hijos directos (lista de secciones / marca + bloque derecho) y
  // `top-nav.css` lo coloque entero pegado a la derecha.
  const areaDerecha = createSafeElement('div', { className: 'top-nav-derecha' });
  const areaSesion = crearAreaSesion(sesion, slotSesion, accesoSesion);
  areaDerecha.append(areaSesion);

  nav.append(elementoIzquierdo, areaDerecha);

  return {
    elemento: nav,
    // Sin listeners propios que retirar (ver cabecera del módulo): los
    // enlaces son `<a href>` nativos. `destruir()` solo desconecta el
    // elemento del DOM, mismo contrato de limpieza que el resto de
    // componentes/páginas del proyecto.
    destruir: () => {
      nav.remove();
    },
  };
}
